import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, Save, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const AboutPageManagement = () => {
  const [sections, setSections] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    section: '',
    title: '',
    content: '',
    image_url: '',
    order_index: 0,
  });

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await fetch('http://localhost/api/pages.php?type=about', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Bölümler yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setSections(data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      toast.error('Bölümler yüklenirken hata oluştu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'order_index' ? parseInt(value) || 0 : value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    // Dosya boyutu kontrolü (25MB)
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Dosya boyutu 25MB\'dan büyük olamaz');
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await fetch('http://localhost/api/upload.php', {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload,
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        console.error('Response text:', text);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Görsel yüklenirken hata oluştu');
        return;
      }

      // URL'yi kontrol et ve düzelt
      let imageUrl = data.url || data.image_url || '';

      // Eğer sadece filename varsa, tam URL oluştur
      if (!imageUrl && data.filename) {
        imageUrl = `/uploads/${data.filename}`;
      }

      // Relative path'i tam URL'ye çevir
      if (imageUrl && !imageUrl.startsWith('http')) {
        if (imageUrl.startsWith('/uploads/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else if (imageUrl.startsWith('/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else {
          imageUrl = `http://localhost/uploads/${imageUrl}`;
        }
      }

      if (imageUrl) {
        console.log('Setting image URL:', imageUrl);
        setFormData(prev => {
          const updated = { ...prev, image_url: imageUrl };
          console.log('Updated formData:', updated);
          return updated;
        });
        toast.success('Görsel başarıyla yüklendi');
      } else {
        console.error('No image URL found in response:', data);
        toast.error('Görsel URL alınamadı');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingSection ? 'PUT' : 'POST';
      const url = 'http://localhost/api/pages.php?type=about';
      
      // Section'ı otomatik oluştur (başlıktan slug oluştur)
      const section = editingSection?.section || formData.title.toLowerCase()
        .replace(/ş/g, 's')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/ı/g, 'i')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          section: section
        }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Bölüm kaydedilirken hata oluştu');
        return;
      }

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      toast.success(editingSection ? 'Bölüm güncellendi' : 'Bölüm eklendi');
      setIsModalOpen(false);
      setEditingSection(null);
      setFormData({
        section: '',
        title: '',
        content: '',
        image_url: '',
        order_index: 0,
      });
      fetchSections();
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Bölüm kaydedilirken hata oluştu');
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData({
      title: section.title || '',
      content: section.content || '',
      image_url: section.image_url || '',
      order_index: section.order_index || 0,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (sectionKey) => {
    setDeleteItemId(sectionKey);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;

    try {
      const response = await fetch(`http://localhost/api/pages.php?type=about&section=${deleteItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Bölüm silinirken hata oluştu');
        return;
      }

      toast.success('Bölüm silindi');
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
      fetchSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Bölüm silinirken hata oluştu');
    }
  };

  const openAddModal = () => {
    setEditingSection(null);
    setFormData({
      title: '',
      content: '',
      image_url: '',
      order_index: sections.length,
    });
    setIsModalOpen(true);
  };

  return (
    <AdminLayout
      addButton={
        <Button
          onClick={openAddModal}
          className="bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700 text-white flex items-center gap-2"
        >
          <Plus size={20} />
          Yeni Bölüm
        </Button>
      }
    >
      <div>
        <div className="mt-6">
          {sections.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz bölüm yok</h3>
              <p className="text-gray-600 mb-6">Yeni bir bölüm ekleyerek başlayın</p>
              <Button onClick={openAddModal} className="bg-gradient-to-r from-red-900 to-red-800">
                İlk Bölümü Ekle
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {sections
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((section) => (
                  <div
                    key={section.section}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 bg-red-900/10 text-red-900 text-xs font-semibold rounded-full">
                            Sıra: {section.order_index || 0}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{section.title || 'Başlıksız'}</h3>
                        <p className="text-gray-600 text-sm line-clamp-3 mb-3">
                          {section.content || 'İçerik yok'}
                        </p>
                        {section.image_url && (
                          <img
                            src={section.image_url}
                            alt={section.title}
                            className="w-32 h-32 object-cover rounded-lg mb-3"
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          onClick={() => handleEdit(section)}
                          variant="outline"
                          size="sm"
                        >
                          <Edit size={16} className="mr-1" />
                          Düzenle
                        </Button>
                        <Button
                          onClick={() => handleDelete(section.section)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSection ? 'Bölümü Düzenle' : 'Yeni Bölüm Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık</label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Bölüm başlığı"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">İçerik</label>
              <Textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Bölüm içeriği"
                rows={8}
              />
            </div>

            {/* Görsel yükleme - Sadece story ve founder için göster */}
            {(editingSection?.section === 'story' || editingSection?.section === 'founder' || (!editingSection && (formData.title.toLowerCase().includes('hikaye') || formData.title.toLowerCase().includes('kurucu')))) && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Görsel Yükle</label>
                <div className="grid grid-cols-2 gap-4">
                  {/* Sol: Yükleme Alanı */}
                  <div
                    className="relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-900 hover:bg-red-50/50 transition-all cursor-pointer group"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.add('border-red-900', 'bg-red-50/50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-red-900', 'bg-red-50/50');
                    }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-red-900', 'bg-red-50/50');
                      const file = e.dataTransfer.files?.[0];
                      if (file && file.type.startsWith('image/')) {
                        await handleImageUpload(file);
                      } else {
                        toast.error('Lütfen geçerli bir resim dosyası seçin');
                      }
                    }}
                    onClick={() => {
                      const input = document.getElementById(`image-upload-input-${editingSection?.section || 'new'}`);
                      input?.click();
                    }}
                  >
                    <input
                      id={`image-upload-input-${editingSection?.section || 'new'}`}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          await handleImageUpload(file);
                        }
                      }}
                    />
                    {!formData.image_url ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-gray-100 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                          <Upload className="w-8 h-8 text-gray-400 group-hover:text-red-900 transition-colors" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Görsel sürükle ve bırak
                          </p>
                          <p className="text-xs text-gray-500">
                            veya tıklayarak seç
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          PNG, JPG, GIF (max. 25MB)
                        </p>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-xs text-gray-500">Görseli değiştirmek için tıklayın</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sağ: Önizleme */}
                  <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 flex items-center justify-center min-h-[200px]">
                    {formData.image_url ? (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img 
                          key={formData.image_url}
                          src={formData.image_url} 
                          alt="Önizleme" 
                          className="max-w-full max-h-48 object-contain rounded-lg"
                          onError={(e) => {
                            console.error('Image load error for URL:', formData.image_url);
                            e.target.style.display = 'none';
                            const errorDiv = e.target.parentElement.querySelector('.image-error');
                            if (errorDiv) {
                              errorDiv.style.display = 'block';
                            }
                          }}
                          onLoad={() => {
                            console.log('Image loaded successfully:', formData.image_url);
                          }}
                        />
                        <div className="image-error hidden text-center text-red-500 text-xs">
                          <p>Görsel yüklenemedi</p>
                          <p className="text-gray-400 break-all text-[10px] mt-1">{formData.image_url}</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData(prev => ({ ...prev, image_url: '' }));
                            const input = document.getElementById(`image-upload-input-${editingSection?.section || 'new'}`);
                            if (input) input.value = '';
                          }}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors z-10"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center text-gray-400">
                        <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">Önizleme</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sıra</label>
              <Input
                type="number"
                name="order_index"
                value={formData.order_index}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700"
              >
                <Save size={16} className="mr-2" />
                {editingSection ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Bu bölümü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteItemId(null);
              }}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AboutPageManagement;

